import moment from 'moment-timezone';
import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { DepositBankAccount } from '../deposit-bank-account';
import { Organization } from '../organization';
import { PaymentAttempt } from '../payment-attempt';
import { TransactionAttachment } from '../transaction-attachment';
import { UnifiedBankAccount } from '../unified-bank-account';
import { BankReconciliationTransaction } from '../bank-reconciliation-transaction';
import { PaymentItem } from '../payment-item';
import { TransactionRule } from './transaction-rules/transaction-rule';
import { JournalEntry } from './journal-entries/journal-entry';
import { Transfer } from './transfers/transfer';
import { Category } from '../accounting/category';
import { StripePayout } from './stripe-payout';
import { Vendor } from 'app/models/entities/vendors/vendor';
import { Payable } from '../payable';
import { LedgerEntry } from '../ledger-entry';
import { getConfidenceLevel } from '../../ai-statistics.model';
import Moment = moment.Moment;

export class Transaction
{
    /**
     * SD-8152: the backend's Transaction::TRANSACTION_TYPE_RETURNED_PAYMENT. Named here rather than
     * spelled as a literal at each call site, because deleting a row of this type does not delete a
     * transaction — it undoes the return — so the branches that key on it are behavioural, not cosmetic.
     */
    static readonly TRANSACTION_TYPE_RETURNED_PAYMENT = 'returned_payment';

    /**
     * SD-8152: the confirm-dialog copy for deleting a selection that contains at least one
     * "Returned Payment" row, or null when it contains none (the caller then keeps its own ordinary
     * delete copy, which differs between the v1 and v2 dialogs).
     *
     * Lives here, once, because THREE entry points hit the same backend delete action — the
     * transactions list, and Bank Reconciliation Details' bulk and row-menu deletes — and a reviewer
     * caught two of them shipping the plain "remove 1 transaction?" copy while the backend was
     * quietly undoing a return. Copy-pasting the branch a third time is how they drift apart again.
     *
     * @param rows       the selected rows whose types are known
     * @param totalCount how many transactions the delete will actually submit; may exceed
     *                   rows.length if a caller cannot resolve every row object. In that case a
     *                   selection with no visible returned payment is warned about rather than waved
     *                   through (see the unresolved-selection branch below), and one WITH a visible
     *                   returned payment states the ordinary-deletion count as a lower bound plus an
     *                   explicit note about the unresolved remainder — never suppressing both
     *                   disclosures, since every submitted id is still deleted either way.
     */
    static returnedPaymentDeleteCopy(
        rows: Transaction[],
        totalCount: number,
    ): { title: string; content: string; confirmButton: string } | null {
        const returnedCount = rows.filter(
            (row) => row.transactionType === Transaction.TRANSACTION_TYPE_RETURNED_PAYMENT,
        ).length;

        if (returnedCount === 0) {
            // The caller could not resolve every submitted id to a row. Bank Reconciliation Details'
            // selection is id-only and survives paging, while its `selected` getter only resolves
            // against the current page — so a returned payment ticked on page 1 is invisible here
            // when the delete is fired from page 2, and the backend still undoes its return.
            // "No returned payment among the rows we can see" is not "no returned payment in the
            // selection", so warn on the possibility rather than show the plain delete copy.
            if (rows.length < totalCount) {
                const one = totalCount === 1;

                return {
                    title: `Delete transaction${one ? '' : 's'}?`,
                    content: (one
                            ? `The selected transaction is not on this page, so we can't confirm `
                                + `whether it is a returned payment. `
                            : `Some of the ${totalCount} selected transactions are not on this page, `
                                + `so we can't confirm whether any of them is a returned payment. `)
                        + `Deleting a returned payment undoes the return rather than removing the `
                        + `transaction: the payment is restored and applied again, the unit's balance `
                        + `and payment history are updated, and reversing entries are posted to the `
                        + `general ledger. If the invoice a payment settled has since been deleted, `
                        + `that payment comes back as an account credit instead. `
                        // This branch replaces the caller's own delete copy, which is where the
                        // ledger-wide scope was stated, so it has to carry that clause itself or a
                        // cross-page bulk delete silently loses it. Worded without naming the
                        // reconciliation: all three entry points share this method, and the
                        // transactions list is not inside a reconciliation.
                        + `Deleted transactions are removed from your entire transactions ledger.`,
                    confirmButton: `Yes, Delete Transaction${one ? '' : 's'}`,
                };
            }

            return null;
        }

        const returnPlural = returnedCount === 1 ? '' : 's';
        // Every resolved non-returned row is CERTAINLY being deleted, so this is a known lower bound
        // even when the selection is only partially resolved. It must not collapse to 0 in that case:
        // doing so dropped both the "other N will simply be deleted" sentence AND the ledger-scope
        // warning while deleteTransactions() still submitted every id, so the user confirmed deleting
        // N ledger transactions believing they were only reversing one return.
        const otherCount = rows.length - returnedCount;
        const otherPlural = otherCount === 1 ? '' : 's';
        // Submitted ids that could not be resolved to a row: the selection survives paging while the
        // caller's rows only cover the current page.
        const unresolvedCount = Math.max(0, totalCount - rows.length);
        const unresolvedPlural = unresolvedCount === 1 ? '' : 's';
        const deletesOthers = otherCount > 0 || unresolvedCount > 0;

        let content = `This will undo the return${returnPlural}, not just delete the `
            + `transaction${totalCount === 1 ? '' : 's'}. `
            + `The payment${returnPlural} will be restored and applied again, the unit's balance and `
            + `payment history will be updated, and reversing entries will be posted to the general `
            + `ledger. If the invoice a payment settled has since been deleted, that payment will `
            + `come back as an account credit instead.`;

        if (otherCount > 0) {
            // Pluralising only off the returned count hid the fact that ordinary transactions were
            // being deleted in the same submit. Worded as a minimum when the count is a lower bound,
            // rather than stated as exact or suppressed entirely.
            content += unresolvedCount > 0
                ? ` At least ${otherCount} other selected transaction${otherPlural} will simply be `
                    + `deleted, not reversed.`
                : ` The other ${otherCount} selected transaction${otherPlural} will simply be `
                    + `deleted, not reversed.`;
        }

        if (unresolvedCount > 0) {
            content += ` ${unresolvedCount} further selected transaction${unresolvedPlural} `
                + `${unresolvedCount === 1 ? 'is' : 'are'} not on this page, so we can't confirm `
                // "any of them is", matching the sibling string in the returnedCount === 0 branch:
                // the predicate is singular, so the plural verb reads as a slip.
                + `whether ${unresolvedCount === 1 ? 'it is' : 'any of them is'} a returned payment too.`;
        }

        if (deletesOthers) {
            // The scope disclosure the ordinary delete copy carries. Without it this branch could
            // describe a reversal while silently removing transactions ledger-wide.
            content += ` Deleted transactions are removed from your entire transactions ledger.`;
        }

        // Every non-reversed entry being submitted, resolved or not: deriving this from otherCount
        // alone let the title read "delete transaction?" while an unresolved remainder meant several
        // were actually going.
        const deletePlural = otherCount + unresolvedCount === 1 ? '' : 's';

        return {
            title: deletesOthers
                ? `Reverse returned payment${returnPlural} and delete transaction${deletePlural}?`
                : `Reverse ${returnedCount === 1 ? 'this' : 'these'} returned payment${returnPlural}?`,
            content,
            confirmButton: deletesOthers
                ? 'Yes, Reverse and Delete'
                : `Yes, Reverse Return${returnPlural}`,
        };
    }

    id: number;
    organizationId: number;
    parentId: number;
    vendorId?: number;
    categoryId: number;
    transactionRuleId: number;
    ownerId: number;
    ownerType: string;
    bankAccountId: number;
    amount: number;
    originalAmount: number;
    description: string = '';
    memo: string = '';
    transactionType: string = null;
    legfiDeposit: boolean = false;
    transactionDate: Moment;
    transactionDateString: string = '';
    unitId: number;
    excluded: boolean = false;
    approved: boolean = false;
    journalEntry: boolean = false;
    wasMatched: boolean;
    matchedDate?: string;
    matchedText?: string;
    transactionMatchId: number | null;
    importedAt: Moment;
    updatedAt: Moment;
    deletedAt: Moment;
    billPaymentId: number;
    linkedPayment: { id: number; model: string; amount: number; sendDate: string; checkNumber: string };
    checkNumber: string;
    isExternalRevenue: boolean;
    allianceTransactionId: number;
    stripeTreasuryTransactionId: number;
    disputeStatus: string = null;
    disputeFiledAt: string = null;
    disputeResolvedAt: string = null;
    // SD-7601: parsed card-description lines (null for non-card rows).
    cardMerchant: string = null;
    cardSecondaryLine: string = null;
    isPending: boolean;
    isPayable: boolean;
    convertToTransaction: boolean;
    // SD-8074: fields the API says may be edited on this transaction. Treasury Balance rows
    // and their invoice children return a restricted set so the detail form can disable the rest.
    // null when the API did not supply the list (older payloads) — treated as "no restriction".
    editableFields: string[] = null;

    aiSuggestion?: {
        suggestedCategoryId: number;
        suggestedCategory: string;
        categoryConfidence: number;  // 0-100
        categoryConfidenceLevel: string;  // 'Low', 'Medium', 'High' based on categoryConfidence
        suggestedVendorId?: number;
        suggestedVendor?: string;
        vendorConfidence?: number;
        vendorConfidenceLevel?: string;  // 'Low', 'Medium', 'High' based on vendorConfidence
        isPending: boolean;
        categoryReasons?: Array<{
            type: string;
            text: string;
            weight: number;
            formattedText: string;
        }>;
        vendorReasons?: Array<{
            type: string;
            text: string;
            weight: number;
            formattedText: string;
        }>;
    };

    attachments: TransactionAttachment[] = [];
    organization: Organization;
    bankAccount: DepositBankAccount | UnifiedBankAccount;
    account: DepositBankAccount | UnifiedBankAccount;
    category: Category;
    vendor: Vendor;
    paymentAttempts: PaymentAttempt[] = [];
    refundedPaymentAttempts: PaymentAttempt[];
    transfer: Transfer;
    children: Transaction[] = [];
    reference: PaymentAttempt | PaymentItem | Payable;
    referenceId: number;
    journalEntries: JournalEntry[];
    transactionRule: TransactionRule = null;
    stripePayout: StripePayout;
    owner: UnifiedBankAccount;
    ledgerEntries: LedgerEntry[] = [];

    // Template control public vars
    public selected: boolean;

    public hidden: boolean = false;
    public checked: boolean = false;
    public showDepositDetail: boolean = false;
    public loadingDetail: boolean = false;
    public isExpandableDeposit: boolean = false;
    public missingFunds: boolean = false;
    public missingAmount: number = 0;
    public isOfflinePayment: boolean = false;

    public editCategory: { id: number, type: string } = null;

    public bankReconciliationTransaction: BankReconciliationTransaction;
    public selectedChild: Transaction = null;

    constructor(transaction: any) {
        this.id = transaction.id || null;
        this.organizationId = transaction.organizationId || null;
        this.parentId = transaction.parentId || null;
        this.bankAccountId = transaction.ownerId || null;
        this.categoryId = transaction.categoryId || null;

        this.referenceId = transaction.referenceId ?? null;

        if (transaction.reference) {
            if (transaction.referenceType.indexOf('PaymentAttempt') !== -1) {
                this.reference = new PaymentAttempt(transaction.reference);
            }
            if (transaction.referenceType.indexOf('PaymentItem') !== -1) {
                this.reference = new PaymentItem(transaction.reference);
            }
            if (transaction.referenceType.indexOf('Payable') !== -1) {
                this.reference = new Payable(transaction.reference);
            }
        }

        this.children = transaction.children ? transaction.children.map(c => new Transaction(c)) : [];
        this.owner = transaction.owner ? new UnifiedBankAccount(transaction.owner) : null;

        this.isExternalRevenue = transaction.isExternalRevenue ?? false;
        this.stripeTreasuryTransactionId = transaction.stripeTreasuryTransactionId ?? null;
        this.disputeStatus = transaction.disputeStatus ?? transaction.dispute_status ?? null;
        this.disputeFiledAt = transaction.disputeFiledAt ?? transaction.dispute_filed_at ?? null;
        this.disputeResolvedAt = transaction.disputeResolvedAt ?? transaction.dispute_resolved_at ?? null;
        this.cardMerchant = transaction.cardMerchant ?? transaction.card_merchant ?? null;
        this.cardSecondaryLine = transaction.cardSecondaryLine ?? transaction.card_secondary_line ?? null;
        this.isPending = transaction.isPending ?? false;
        this.isPayable = transaction.isPayable ?? false;
        this.vendorId = transaction.vendorId ?? null;
        this.transactionRuleId = transaction.transactionRuleId ?? null;

        this.ownerId = transaction.ownerId ?? null;
        this.ownerType = transaction.ownerType ?? null;

        this.excluded = transaction.excluded ?? false;
        this.approved = transaction.approved ?? false;
        this.billPaymentId = transaction.billPaymentId ?? null;
        this.linkedPayment = transaction.linkedPayment ?? null;
        this.checkNumber = transaction.checkNumber ?? null;
        this.journalEntry = transaction.journalEntry ?? false;
        this.wasMatched = transaction.wasMatched ?? false;
        this.matchedDate = transaction.matchedDate;
        this.transactionMatchId = transaction.transactionMatchId ?? null;
        if (this.wasMatched) {
            this.matchedText = 'Automatically matched from Bill Pay transaction on ' + this.matchedDate + '.';
        }

        this.amount = transaction.amount;
        this.originalAmount = transaction.originalAmount;
        this.description = transaction.description;
        this.memo = transaction.memo;

        this.legfiDeposit = transaction.transactionType === 'stripe_deposit';
        this.transactionType = transaction.transactionType;
        this.editableFields = transaction.editableFields ?? transaction.editable_fields ?? null;

        const timezone = LegFiJwtService.getTimezone();
        if (transaction.transactionDate) {
            this.transactionDate = moment.tz(transaction.transactionDate, 'YYYY-MM-DD HH:mm:ss', timezone);
            this.transactionDateString = this.transactionDate.format('MM/DD/YYYY');
        }
        if (transaction.importedAt) {
            this.importedAt = moment.tz(transaction.importedAt, 'YYYY-MM-DD HH:mm:ss', timezone);
        }
        if (transaction.deletedAt) {
            this.deletedAt = moment.tz(transaction.deletedAt, 'YYYY-MM-DD HH:mm:ss', timezone);
        }
        if (transaction.updatedAt) {
            this.updatedAt = moment.tz(transaction.updatedAt, 'YYYY-MM-DD HH:mm:ss', timezone);
        }

        this.transfer = (transaction.transfer) ? new Transfer(transaction.transfer) : null;

        if (transaction.unitId) {
            this.unitId = transaction.unitId;
        }
        if (transaction.organization) {
            this.organization = new Organization(transaction.organization);
        }
        if (transaction.owner && transaction.ownerType === 'App\\Models\\Entities\\UnifiedBankAccount') {
            this.bankAccount = new UnifiedBankAccount(transaction.owner);
            this.account = new UnifiedBankAccount(transaction.owner);
        }
        if (transaction.category) {
            this.category = new Category(transaction.category);
        }
        if (transaction.attachments) {
            this.attachments = transaction.attachments.map((attachment) => {
                return new TransactionAttachment(attachment);
            });
        }
        if (transaction.journalEntries) {
            this.journalEntries = transaction.journalEntries.map((je) => {
                return new JournalEntry(je);
            });
        }
        if (transaction.paymentAttempts) {
            this.paymentAttempts = transaction.paymentAttempts.map(
                    (paymentAttempt) => {
                        return new PaymentAttempt(paymentAttempt);
                    },
            );
        }

        if (transaction.refundedPaymentAttempts) {
            this.refundedPaymentAttempts = transaction.refundedPaymentAttempts.map(
                    (paymentAttempt) => {
                        return new PaymentAttempt(paymentAttempt);
                    },
            );
        }

        this.transactionRule = transaction.transactionRule
                ? new TransactionRule(transaction.transactionRule)
                : null;

        if (this.children.length > 0) {
            this.isExpandableDeposit = true;
            if (this.transactionType !== 'lockbox_deposit') {
                this.isOfflinePayment = true;
            }
        }

        if (this.legfiDeposit) {
            let childTotal = 0;
            this.children.forEach((child) => {
                childTotal += child.amount;
            });
            if (childTotal > this.originalAmount) {
                this.missingFunds = true;
                this.missingAmount = this.originalAmount - childTotal;
            }
        }

        if (transaction.bankReconciliationTransaction) {
            this.bankReconciliationTransaction = new BankReconciliationTransaction(transaction.bankReconciliationTransaction);
        }

        if (transaction.stripePayout) {
            this.stripePayout = new StripePayout(transaction.stripePayout);
        }

        if (transaction.ledgerEntries) {
            this.ledgerEntries = transaction.ledgerEntries.map((entry: any) => new LedgerEntry(entry));
        }

        if (transaction.aiSuggestion) {
            this.aiSuggestion = {
                suggestedCategoryId: transaction.aiSuggestion.suggestedCategoryId,
                suggestedCategory: transaction.aiSuggestion.suggestedCategory,
                categoryConfidence: transaction.aiSuggestion.categoryConfidence,
                categoryConfidenceLevel: getConfidenceLevel(transaction.aiSuggestion.categoryConfidence),
                suggestedVendorId: transaction.aiSuggestion.suggestedVendorId,
                suggestedVendor: transaction.aiSuggestion.suggestedVendor,
                vendorConfidence: transaction.aiSuggestion.vendorConfidence,
                vendorConfidenceLevel: getConfidenceLevel(transaction.aiSuggestion.vendorConfidence),
                isPending: transaction.aiSuggestion.isPending,
                categoryReasons: transaction.aiSuggestion.categoryReasons || [],
                vendorReasons: transaction.aiSuggestion.vendorReasons || []
            };
        }
    }

    isPayableReference(reference: any): reference is Payable {
        return reference && (reference as Payable).invoiceDate !== undefined;
    }

    /**
     * SD-8074: Treasury Balance transactions are read-only in the ledger — they cannot be
     * approved/undone or edited by users. Used to hide approve/undo actions on the transactions list.
     *
     * NOTE: This is intentionally narrower than the backend's tree-aware `isTreasuryTransaction()`.
     * It reflects only this row's OWN type (`transactionType === 'treasury'`), with no awareness of
     * ancestor/parent transactions. That is correct for its current uses — all list-level parent rows,
     * where invoice-child locking flows through the API's `editableFields` map instead. Do NOT reuse
     * `isTreasury` on a drilled-in child expecting tree-aware behavior; use `editableFields` there.
     */
    get isTreasury(): boolean {
        return this.transactionType === 'treasury';
    }

    /**
     * SD-8113: a transfer transaction (a money movement between two accounts) carries a Transfer
     * record.
     *
     * SD-9071: this answers ONLY "is this a transfer", and must not be read as "is this row
     * locked". It was the whole delete-checkbox predicate under SD-8113, which locked every
     * transfer when only Financial Account ("Treasury Balance") ones were meant to be locked.
     * The lock now lives in `TransactionsTableComponent.isTreasuryRestrictedTransfer`, which
     * combines this with the Treasury account check; core mirrors it in
     * `TransactionsService::deleteTransactions`.
     */
    get isTransfer(): boolean {
        return !!this.transfer;
    }

    updateDepositCategory() {
        // For payables w/ multiple categories, always show "X Categories"
        // This check has to run first, regardless of whether a category is already set
        if (this.isPayable && this.isPayableReference(this.reference)) {
            const expenseCategories = this.reference.expenseCategories;

            if (expenseCategories && expenseCategories.length > 1) {
                this.category = new Category({
                    name: expenseCategories.length + ' Categories',
                });
                return;
            } else if (expenseCategories && expenseCategories.length === 1 && !this.category) {
                const singleCategory = expenseCategories[0];
                this.category = new Category({
                    name: singleCategory.expenseCategory?.label || 'Uncategorized',
                });
                return;
            }
        }

        if (this.children && this.children.length > 0) {
            let cats = [];
            let hasUncategorizedChild = false;

            for (const child of this.children) {
                if (child.children && child.children.length > 0) {
                    for (const item of child.children) {
                        if (item.category?.name) {
                            cats.push(item.category.name);
                        } else {
                            hasUncategorizedChild = true;
                        }
                    }
                } else if (child.category?.name) {
                    cats.push(child.category.name);
                } else {
                    hasUncategorizedChild = true;
                }
            }

            if (hasUncategorizedChild && this.transactionType === 'lockbox_deposit') {
                this.category = null;
                this.categoryId = null;
                this.aiSuggestion = null;
                return;
            }

            cats = cats.filter((v, i) => cats.indexOf(v) === i);
            if (cats.length === 1) {
                this.category = new Category({
                    name: cats[0],
                });
            } else if (cats.length > 1) {
                this.category = new Category({
                    name: cats.length + ' Categories',
                });
            }
        }
    }
}
